/**
 * rasterVersion.ts
 * ترقيم روابط النُسج بدورة النموذج (?v=run_epoch): يسمح بكاش متصفح طويل (يوم كامل عبر
 * .htaccess) لأن الرابط يتغيّر تلقائياً مع كل دورة جديدة — فيتحمّل الزائر الإطار مرة
 * واحدة ثم يصبح الشريط الزمني وتبديل الطبقات فورياً من الكاش، وهو الحلّ العملي الوحيد
 * لبطء الاستضافة المشتركة (TTFB ~1ث وسرعة ~80KB/s لكل اتصال).
 */

let version = 0;
let resolved = false;
let waiters: Array<() => void> = [];

/** تضبطها الواجهة عند وصول meta.json (run_epoch)، أو 0 عند فشله (تطوير بلا meta). */
export function setRasterVersion(v: number): void {
    version = v;
    resolved = true;
    for (const w of waiters) w();
    waiters = [];
}

/**
 * ينتظر معرفة رقم الدورة قبل أي جلب نسيج — وإلا انطلقت طلبات بلا ?v وخُزّنت يوماً
 * كاملاً (كاش .htaccess الطويل) فعرضت بيانات دورة قديمة بعد تحديث السيرفر.
 */
export function whenRasterVersionKnown(): Promise<void> {
    return resolved ? Promise.resolve() : new Promise((r) => waiters.push(r));
}

/**
 * صيغة ملفّات الراستر المطلوبة من الخادم.
 *
 * PNG هو الافتراضي دائماً — لا يتحوّل إلى WebP إلا حين يُعلن `meta.ext` ذلك، وهو لا
 * يُعلَن إلا بعد اكتمال نظير `.webp` لكل ملفّ `.png` في المجلّد (راجع `raster_ext` في
 * سكربت التوليد). فالطرح آمن بلا تنسيق بين الطرفين: تُولَّد النسخ في الخلفية، ثم تتحوّل
 * الواجهة من تلقائها في أوّل دورة مكتملة، وتعود إلى PNG فوراً إن نقصت.
 *
 * لماذا WebP بلا فقدان تحديداً: القيم مُرمَّزة في البكسلات نفسها (رمادي = القيمة، وRGB
 * للرياح = سرعة/U/V)، فأي ضغط فاقد يُفسد البيانات لا الصورة. قيس الفرق على 21 ملفاً
 * حقيقياً: **‏-15.1%** حجماً مع **تطابق بتّي تامّ** في كلٍّ منها.
 */
let ext: 'png' | 'webp' = 'png';

/** تضبطها الواجهة من `meta.ext`. أي قيمة غير 'webp' تعني البقاء على PNG. */
export function setRasterExt(value: string | undefined): void {
    ext = value === 'webp' ? 'webp' : 'png';
}

/**
 * تجاوز الامتداد/الإصدار لمجلّد لا يتبع دورة النموذج.
 *
 * طبقة شذوذ سطح البحر (`rasters/ocean/`) تُولَّد **يومياً من NOAA لا من دورة GFS**،
 * ولها مانيفستها الخاص بامتداده و`generated_epoch`. لولا هذا التجاوز لحدث عطبان:
 * ‏(١) الامتداد العالمي `webp` يُطلب من مجلّد قد لا تكتمل فيه نظائر webp بعد → 404
 * صامت لكل إطار؛ و(٢) `?v=run_epoch` يتغيّر أربع مرّات يومياً فيُبطل كاش رواستر
 * لم تتغيّر أصلاً — إعادة تنزيل 18 ميغابايت بلا سبب.
 */
export interface RasterUrlOverride {
    ext?: 'png' | 'webp';
    version?: number;
}

/** رابط نسيج مُرقَّم: rasterUrl('rasters/gfs/', 'wind_000.png') → /rasters/gfs/wind_000.png?v=... */
export function rasterUrl(dir: string, file: string, override?: RasterUrlOverride): string {
    // نقطة الاختناق الوحيدة لبناء روابط الراستر — كل المستدعين يمرّون بها ويكتبون
    // `.png`، فتبديل الامتداد هنا يشمل الطبقات والمُعاين والإحماء دفعةً واحدة.
    const useExt = override?.ext ?? ext;
    const useVersion = override?.version ?? version;
    const name = useExt === 'webp' && file.endsWith('.png') ? `${file.slice(0, -4)}.webp` : file;
    const base = `${import.meta.env.BASE_URL}${dir}${name}`;
    return useVersion > 0 ? `${base}?v=${useVersion}` : base;
}

/*
 * ─── سجلّ مصادر الطبقات المستقلّة ─────────────────────────────────────────────
 *
 * أغلب الطبقات تعيش في مجلّد النموذج المختار وتتبع دورته، فيكفيها `rasterUrl(dir, file)`.
 * لكن بعضها **رصدٌ مستقلّ** له مجلّده وامتداده ورقم إصداره (اليوم: شذوذ سطح البحر من
 * NOAA يومياً). ولأن ستّة مواضع في الشيفرة تبني روابط إطارات — الطبقة، والتلميح،
 * والإحماء القريب، والتعبئة الخلفية، ومؤشّر التحميل، وإحماء تبديل النموذج — فإن ترك
 * كلٍّ منها يقرّر بنفسه يعني أن **نسيان واحدٍ منها لا يُكتشف**: خادم التطوير يردّ على
 * المسار الخاطئ بصفحة SPA بحالة **200**، فتظهر في شبكة المتصفّح كطلب ناجح ثم تفشل
 * بصمت في فاكّ الصور. حدث ذلك فعلاً في موضعين بعد أن صُحّحت الأربعة الأخرى.
 *
 * السجلّ يجعل السؤال «أين يعيش هذا النوع؟» يُجاب مرّة واحدة في مكان واحد.
 */
export interface LayerRasterSource {
    dir: string;
    ext: 'png' | 'webp';
    version: number;
}

const layerSources = new Map<string, LayerRasterSource>();

/** تسجيل مصدر مستقلّ لنوع طبقة (أو null لإزالته والعودة إلى مجلّد النموذج). */
export function setLayerRasterSource(type: string, source: LayerRasterSource | null): boolean {
    const current = layerSources.get(type);
    if (!source) {
        if (!current) return false;
        layerSources.delete(type);
        return true;
    }
    if (current && current.dir === source.dir && current.ext === source.ext && current.version === source.version) {
        return false;
    }
    layerSources.set(type, source);
    return true;
}

export function getLayerRasterSource(type: string): LayerRasterSource | undefined {
    return layerSources.get(type);
}

/**
 * رابط إطار طبقة — **البانى الوحيد الذي ينبغي أن يستعمله أي مستدعٍ جديد**.
 * يقرأ السجلّ أوّلاً، ويسقط إلى مجلّد النموذج ودورته حين لا تسجيل.
 */
export function layerFrameUrl(modelDir: string, type: string, idx: number): string {
    const file = `${type}_${String(idx).padStart(3, '0')}.png`;
    const source = layerSources.get(type);
    return source
        ? rasterUrl(source.dir, file, { ext: source.ext, version: source.version })
        : rasterUrl(modelDir, file);
}
